<template>
  <b-form method="POST" @submit.prevent="() => false" class="p-lg-4">
    <b-row v-if="getField('content-url')">
      <b-col>

        <!-- URL -->
        <div class="mb-2"><span><strong>{{ $t('content-fetch.titles.url') }}</strong></span></div>
        <b-form-group label-for="url">
          <b-input-group>
            <input
              id="url"
              type="url"
              class="form-control"
              :placeholder="$t('content-fetch.placeholders.get-content')"
              required
              v-model="form[getField('content-url').key]"
              :class="{ 'is-invalid': !getField('content-url').valid(form[getField('content-url').key]) }"
              @paste="fetchContent"
            />
            <b-input-group-append>
              <b-button variant="primary" @click="fetchContent" :disabled="loading">{{ $t('content-fetch.buttons.get-content') }}</b-button>
            </b-input-group-append>
          </b-input-group>
          <div
            v-if="getField('content-url').valid(form[getField('content-url').key])"
            class="invalid-feedback"
          >{{ getField('content-url').invalidText }}</div>
        </b-form-group>
        <p v-if="prefetchError" class="mt-3">{{ $t('content-fetch.invalid.content-not-imported') }}</p>
        <p v-else-if="!prefetchError && form[getField('content-url').key]" class="mt-3">{{ $t('content-fetch.hints.reload') }}</p>
      </b-col>
    </b-row>

    <template v-if="!loading">
      <b-row class="px-3">
        <b-col class="d-flex justify-content-between align-items-center px-0 pb-2 mb-2 border-bottom">
          <span><strong>{{ $t('content-fetch.titles.content') }}</strong></span>
          <b-button
            :variant="editContent ? 'primary' : 'outline-primary'"
            class="px-3"
            size="sm"
            @click="editContent = !editContent"
          >{{ editContent ? $t('content-fetch.buttons.preview') : $t('content-fetch.buttons.edit') }}</b-button>
        </b-col>
      </b-row>

      <b-row class="pt-lg-4">

        <!-- CONTENT -->
        <b-col lg="8">
          <b-row>
            <b-col>
              <template v-if="getField('title')">
                <b-form-group class="mt-2" v-if="editContent">
                  <b-form-input
                    id="title"
                    type="text"
                    :placeholder="getField('title').placeholder"
                    v-model="form[getField('title').key]"
                    :class="{ 'is-invalid': !getField('title').valid(form[getField('title').key]) }"
                    style="font-size: 18px; font-weight: bold"
                  />
                  <div v-if="!getField('title').valid(form[getField('title').key])" class="invalid-feedback">
                    {{ getField('title').invalidText }}
                  </div>
                </b-form-group>
                <b-form-group class="mt-2" v-else-if="!editContent && !getField('title').valid(form[getField('title').key])">
                  <span class="text-secondary">{{ $t('content-fetch.hints.preview-content') }}</span>
                </b-form-group>
                <h2 v-else>{{ form[getField('title').key] }}</h2>
              </template>

              <!-- Description -->
              <template v-if="getField('description')">
                <rich-text-field
                  v-if="editContent"
                  v-model="form[getField('description').key]"
                  :placeholder="getField('description').placeholder" />
                <b-form-group class="mt-2" v-else-if="!editContent && !form[getField('description').key]">
                  <span class="text-secondary">{{ $t('content-fetch.hints.preview-title') }}</span>
                </b-form-group>
                <rich-text-content v-else :value="form.description"/>
              </template>
            </b-col>
          </b-row>
        </b-col>

        <!-- DETAILS -->
        <b-col lg="4" class="pl-2 pl-lg-4">
          <b-row class="px-3">
            <content-card
              v-if="!editContent && preview === 'CONTENT-SHARE' && form.title"
              :isPreview="true"
              :hideFooter="true"
              :entry="form"
            />

            <b-col v-else-if="!editContent && preview === 'CONTENT-SHARE' && !form.title">
              <b-card>
                <span class="text-secondary">{{ $t('content-fetch.hints.highlight') }}</span>
              </b-card>
            </b-col>

            <template v-else>
              <!-- Image -->
              <b-col cols="12" v-if="getField('imageURL') || getField('image')">
                <b-tabs content-class="mt-3" justified>
                  <!-- Image File Tab -->
                  <b-tab
                    v-if="getField('image')"
                    :title="getField('image').title"
                  >
                    <asset-block
                      :field="{ type: 'IMAGE' }"
                      v-model="form[getField('image').key]"
                      :state="null"
                    />
                    <p>{{ $t('content-fetch.hints.image-resize') }}</p>
                  </b-tab>
                  <!-- URL Image Tab -->
                  <b-tab
                    v-if="getField('image-url')"
                    :title="getField('image-url').title"
                  >
                    <b-input-group class="mt-3">
                      <b-form-input
                        v-model="form[getField('image-url').key]"
                        :placeholder="getField('image-url').placeholder"
                      />
                    </b-input-group>
                    <p v-if="getField('image-url')" class="mt-3">{{ $t('content-fetch.hints.no-manually-uploaded-image') }}.</p>
                  </b-tab>
                </b-tabs>
              </b-col>

              <hr/>
              <b-col cols="12">

                <!-- Recruiter Email -->
                <b-form-group
                  :label="getField('recruiterEmail').title"
                  label-for="recruiterEmail"
                >
                  <b-form-input
                    id="recruiterEmail"
                    v-model="form[getField('recruiterEmail').key]"
                    :placeholder="getField('recruiterEmail').placeholder"
                  />
                </b-form-group>

                <!-- LinkedIn URL Field -->
                <b-form-group
                  :label="getField('linkedInSearchURL').title"
                  label-for="linkedInSearchURL"
                >
                  <b-form-input
                    id="linkedInSearchURL"
                    v-model="form[getField('linkedInSearchURL').key]"
                    :placeholder="getField('linkedInSearchURL').placeholder"
                  />
                </b-form-group>

                <!-- Location Free Text -->
                <b-form-group
                  :label="getField('locationFreeText').title"
                  label-for="locationFreeText"
                >
                  <b-form-input
                    id="locationFreeText"
                    v-model="form[getField('locationFreeText').key]"
                    :placeholder="getField('locationFreeText').placeholder"
                  />
                </b-form-group>

                <!-- Status Field -->
                <b-form-group
                  :label="getField('status').title"
                  label-for="status"
                >
                  <b-form-select
                    :options="statusList"
                    v-model="form.status"
                  />
                </b-form-group>

              </b-col>
            </template>
          </b-row>

          <!-- BUTTONS -->
          <b-row class="mt-4 px-3">
            <b-col cols="12" class="text-right px-0">
              <b-form-group>
                <b-button v-if="buttons.includes('PUBLISH_AND_SAVE')" size="md" :disabled="!isChanged || !isFormValid" @click.prevent="submitInput()" variant="success">{{ $t('content-fetch.buttons.save') }}</b-button>
              </b-form-group>
            </b-col>
          </b-row>
        </b-col>

      </b-row>
    </template>

    <b-row class="p-5" v-if="loading">
      <b-col class="p-5 d-flex justify-content-center">
        <b-spinner :style="{ height: '32px', width: '32px' }" type="grow" variant="primary"/>
      </b-col>
    </b-row>

  </b-form>
</template>

<script>
import enumsMixin from '@/mixins/enums';
import formValidationMixin from '@src/mixins/formValidation';
import AssetBlock from '@/components/settings/AssetBlock';
import ContentCard from '@/components/content-share/ContentCard';
import RichTextField, { RichTextContent } from '@/components/RichTextField';
import PrefetchContentShareQuery from '@/graphql/queries/PrefetchContentShare.gql';
import BrandBlock from '../contacts-upload/BrandBlock.vue';
import LocationSearch from '@/components/misc/LocationSearch';

export default {
  props: {
    preview: {
      type: String,
      default: 'CONTENT-SHARE',
    },
    value: {
      type: Object,
      default: () => ({}),
    },
    fields: {
      type: Array,
      default() {
        return [
          {
            key: 'url',
            type: 'content-url',
            title: this.$t('content-fetch.labels.url'),
            placeholder: this.$t('content-fetch.placeholders.url'),
            valid(val) {
              if (!val) return true;
              const expr = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)?/gi;
              const regex = new RegExp(expr);
              return val.match(regex);
            },
            invalidText: 'URL is not valid',
          },
          {
            key: 'title',
            type: 'title',
            title: this.$t('content-fetch.labels.title'),
            placeholder: this.$t('content-fetch.placeholders.title'),
            valid: val => val && val.length > 2,
            invalidText: 'The title has to be at least 2 characters',
          },
          {
            key: 'description',
            type: 'description',
            title: this.$t('content-fetch.labels.description'),
            placeholder: this.$t('content-fetch.placeholders.description'),
            valid: () => true,
          },
          {
            key: 'locationFreeText',
            type: 'locationFreeText',
            title: this.$t('jobs.labels.location'),
            placeholder: this.$t('content-fetch.placeholders.location'),
          },
          {
            key: 'recruiterEmail',
            type: 'recruiterEmail',
            title: this.$t('content-fetch.labels.recruiter-email'),
            placeholder: this.$t('content-fetch.placeholders.recruiter-email'),
          },
          {
            key: 'status',
            type: 'status',
            title: this.$t('content-fetch.labels.job-status'),
            placeholder: this.$t('content-fetch.labels.job-status'),
          },
          {
            key: 'linkedInSearchURL',
            type: 'linkedInSearchURL',
            title: this.$t('content-fetch.labels.linkedin-search-url'),
            placeholder: this.$t('content-fetch.placeholders.linkedin-search-url'),
          },
          {
            key: 'image',
            type: 'image',
            title: this.$t('content-fetch.labels.upload-image'),
            placeholder: this.$t('content-fetch.placeholders.image'),
          },
          {
            key: 'imageURL',
            type: 'image-url',
            title: this.$t('content-fetch.labels.image-url'),
            placeholder: this.$t('content-fetch.placeholders.image-url'),
            valid(val) {
              if (!val) return true;
              const expr = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)?/gi;
              const regex = new RegExp(expr);
              return val.match(regex);
            },
            invalidText: this.$t('content-fetch.invalid.url'),
          }
        ];
      },
    },
  },
  components: {
    AssetBlock,
    ContentCard,
    RichTextField,
    RichTextContent,
    BrandBlock,
    LocationSearch
  },
  mixins: [
    enumsMixin,
    formValidationMixin,
  ],
  data: () => ({
    form: {},
    loading: false,
    prefetchError: null,
    editContent: true,
    editHighlight: false,
  }),
  created() {
    this.onResetForm();
  },
  computed: {
    isChanged() {
      const { fields, form, value } = this;
      const original = fields.reduce((out, { key }) => {
        out[key] = value[key];
        return out;
      }, {});
      return JSON.stringify(original) !== JSON.stringify(form);
    },
    isFormValid() {
      const { form } = this;
      return this.fields.reduce((out, { valid, key }) => {
        if (!valid || valid === true) return out;
        return out && valid(form[key]);
      }, true);
    },
    buttons() {
      const { value } = this;
      if (!value || !value.id) {
        return [ 'PUBLISH_AND_SAVE' ];
      }
      return [];
    },
    statusList() {
      return {
        'ACTIVE': this.$t('general.active'),
        'INACTIVE': this.$t('general.inactive'),
      };
    },
  },
  methods: {
    onResetForm() {
      if (this.value) {
        console.log('VALUE: ', this.value);
        this.form = this.fields.reduce((out, { key }) => {
          out[key] = this.value[key];
          return out;
        }, {});
        this.form[this.getField('location').key] = null;
      }
    },
    getField(fieldType) {
      return this.fields.find(({ type }) => type === fieldType);
    },
    async submitInput() {
      try {
        const { isChanged, value, form, isFormValid } = this;
        console.log('submitInput()', form);
        if ((!isChanged && value.id) || !isFormValid) return;

        const input = JSON.parse(JSON.stringify(form));

        if (this.value.status) {
          input.status = this.value.status;
        } else {
          input.status = 'ACTIVE';
        }

        if (input.description === '') input.description = null;

        const create = {
          title: this.form.title,
          image: (this.form.image ? this.form.image.fileLocation : null) || this.form.imageURL || null,
          description: this.form.description,
          status: this.form.status,
          customFields: [
            {
              key: 'linkedInSearchURL',
              name: 'LinkedIn Search URL',
              value: this.form.linkedInSearchURL
            },
            {
              key: 'location',
              name: 'Job Location',
              value: this.form.locationFreeText
            },
            {
              key: 'recruiterEmail',
              name: 'Recruiter Email',
              value: this.form.recruiterEmail
            }
          ]
        };

        if (this.form.location) {
          create.location = {
            id: this.form.location.id
          };
        };

        this.$log.debug({ create });

        this.$emit('input', create);

        this.$bvModal.hide('new-job-by-url-modal');
      } catch (err) {
        console.log(err);
      }
    },
    async fetchContent() {
      const { form } = this;
      const { key, valid } = this.getField('content-url') || {};
      if (!key || !form[key] || !valid(form[key])) return;
      this.loading = true;
      try {
        const { data: { company } } = await this.$apollo.query({
          query: PrefetchContentShareQuery,
          variables: { url: form[key] },
          fetchPolicy: 'network-only',
        });
        const updatedForm = { ...form };
        const { contentSharePrefetch } = company || {};
        const { title, content, image } = contentSharePrefetch || {};
        if (this.getField('title')) updatedForm[this.getField('title').key] = title || updatedForm[this.getField('title').key];
        if (this.getField('image-url')) updatedForm[this.getField('image-url').key] = image || updatedForm[this.getField('image-url').key];
        if (this.getField('description')) updatedForm[this.getField('description').key] = content || updatedForm[this.getField('description').key];
        console.log('UPDATED FORM: ', updatedForm);
        this.form = updatedForm;
        console.log('FORM', this.form);
        console.log(updatedForm === this.form);
      } catch (error) {
        console.log(error);
        this.prefetchError = error;
      }
      this.loading = false;
    }
  },
  watch: {
    value() {
      this.onResetForm();
    },
    prefetchError() {
      this.editContent = true;
      this.editHighlight = true;
    },
  },
};
</script>

<style lang="scss">
#title::placeholder {
  color: #aaa;
  font-weight: 400;
  opacity: 1;
}
</style>



// WEBPACK FOOTER //
// src/components/misc/ContentFetch.vue